import { useState, useEffect, useCallback } from 'react';
import {
  FolderSearch, Upload, RefreshCw, CheckCircle2, Folder,
  FolderOpen, ArrowLeft, Eye, HardDrive, ChevronRight, FileText,
} from 'lucide-react';
import ingestionService from '../api';

const UPLOAD_ROOT = '/home/ubuntu/ingestion/uploads/docugraph';

function displayUploadPath(fullPath) {
  if (fullPath && fullPath.startsWith(UPLOAD_ROOT)) {
    const rel = fullPath.slice(UPLOAD_ROOT.length).replace(/^\//, '');
    return rel ? `uploads/${rel}` : 'uploads';
  }
  return fullPath || '';
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Folder browser modal
function FolderBrowser({ onSelect, onClose }) {
  const [mode, setMode] = useState('server');
  const [browseData, setBrowseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const navigateTo = useCallback(async (path) => {
    setLoading(true);
    setError('');
    try {
      const data = await ingestionService.browseFolders(path);
      setBrowseData(data);
    } catch (err) {
      setError(err?.message ?? 'Failed to browse');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (mode === 'server') navigateTo(); }, [navigateTo, mode]);

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    setUploadError('');
    try {
      const result = await ingestionService.uploadFiles(selectedFiles);
      onSelect(result.folder_path);
    } catch (err) {
      setUploadError(err?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const supportedExts = ['.pdf', '.docx', '.doc', '.txt', '.md', '.csv', '.json', '.xlsx', '.xls', '.pptx', '.ppt', '.html', '.htm', '.rtf'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-primaryDark" />
            <h3 className="font-semibold text-gray-900">Select Document Source</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        <div className="flex border-b">
          {['server', 'local'].map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                mode === m ? 'text-primaryDark border-b-2 border-blue-600 bg-blue-50/50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {m === 'server' ? <><HardDrive className="w-4 h-4" />Server Folders</> : <><Upload className="w-4 h-4" />Local Machine</>}
            </button>
          ))}
        </div>

        {mode === 'server' && (
          <>
            {browseData && (
              <div className="flex items-center gap-2 px-6 py-3 bg-gray-50 border-b text-sm">
                {browseData.parent_path && (
                  <button onClick={() => navigateTo(browseData.parent_path)} className="p-1 rounded hover:bg-gray-200">
                    <ArrowLeft className="w-4 h-4 text-gray-500" />
                  </button>
                )}
                <code className="text-gray-700 font-mono text-xs flex-1 truncate">
                  {browseData.display_path || browseData.current_path}
                </code>
                <button
                  onClick={() => onSelect(browseData.current_path)}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700"
                >
                  Select This Folder
                </button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto px-6 py-3">
              {loading ? (
                <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-primary" /></div>
              ) : error ? (
                <p className="text-red-600 text-center py-8">{error}</p>
              ) : browseData?.directories?.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No subdirectories</p>
              ) : (
                <div className="space-y-1">
                  {browseData?.directories?.map((dir) => (
                    <button
                      key={dir.path}
                      onClick={() => navigateTo(dir.path)}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors text-left group"
                    >
                      <Folder className="w-5 h-5 text-amber-500 group-hover:hidden" />
                      <FolderOpen className="w-5 h-5 text-amber-600 hidden group-hover:block" />
                      <span className="text-gray-800 font-medium">{dir.name}</span>
                      <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {mode === 'local' && (
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            <label
              className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 hover:bg-blue-50/30 transition-colors cursor-pointer block"
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                setSelectedFiles(Array.from(e.dataTransfer.files));
                setUploadError('');
              }}
            >
              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-700 font-medium">Drop files here or click to select</p>
              <p className="text-gray-500 text-sm mt-1">Supported: {supportedExts.join(', ')}</p>
              <input
                type="file"
                multiple
                accept={supportedExts.join(',')}
                className="sr-only"
                onChange={(e) => { setSelectedFiles(Array.from(e.target.files || [])); setUploadError(''); }}
              />
            </label>
            {selectedFiles.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
                    ({formatSize(selectedFiles.reduce((s, f) => s + f.size, 0))})
                  </span>
                  <button onClick={() => setSelectedFiles([])} className="text-xs text-gray-400 hover:text-red-500">Clear all</button>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1 bg-gray-50 rounded-lg p-2">
                  {selectedFiles.slice(0, 30).map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs px-2 py-1">
                      <span className="text-gray-700 truncate flex-1">{f.name}</span>
                      <span className="text-gray-400 ml-2">{formatSize(f.size)}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            )}
            {uploadError && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{uploadError}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

const ProjectList = () => {
  const [folders, setFolders] = useState([]);
  const [folderPath, setFolderPath] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBrowser, setShowBrowser] = useState(false);
  const [previewPath, setPreviewPath] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadFolders = useCallback(async () => {
    try {
      const data = await ingestionService.listFolders();
      setFolders(Array.isArray(data) ? data : []);
    } catch {
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadFolders(); }, [loadFolders]);

  useEffect(() => {
    if (!previewPath) { setPreview(null); return; }
    setPreviewLoading(true);
    ingestionService.previewFolder(previewPath)
      .then(setPreview)
      .catch(() => setPreview(null))
      .finally(() => setPreviewLoading(false));
  }, [previewPath]);

  const handleScan = async (path) => {
    const target = (path ?? folderPath).trim();
    if (!target) return;
    setFolderPath(target);
    setScanning(true);
    setScanResult(null);
    setPreviewPath(null);
    try {
      const result = await ingestionService.scanFolder(target);
      setScanResult(result);
      await loadFolders();
    } catch (err) {
      setScanResult({ error: err?.message ?? 'Scan failed' });
    } finally {
      setScanning(false);
    }
  };

  const handleBrowseSelect = (path) => {
    setFolderPath(path);
    setShowBrowser(false);
    setPreviewPath(path);
  };

  return (
    <div className="space-y-6">
      {/* Scan card */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 rounded-t-lg">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <FolderSearch className="w-5 h-5 text-primaryDark" />
            Scan Folder
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">Browse to a directory or type a path to extract text from supported documents</p>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex flex-wrap sm:flex-nowrap gap-2">
            <input
              type="text"
              placeholder="/uploads"
              value={folderPath}
              onChange={(e) => { setFolderPath(e.target.value); setPreviewPath(null); }}
              className="w-full sm:flex-1 sm:min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => setShowBrowser(true)}
              className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 whitespace-nowrap"
            >
              <Folder className="w-4 h-4" />
              Browse
            </button>
            {folderPath.trim() && !previewPath && (
              <button
                onClick={() => setPreviewPath(folderPath.trim())}
                className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 whitespace-nowrap"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
            )}
            <button
              onClick={() => handleScan()}
              disabled={scanning || !folderPath.trim()}
              className="flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
            >
              {scanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {scanning ? 'Scanning...' : 'Scan'}
            </button>
          </div>

          {/* Preview */}
          {previewPath && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900 text-sm flex items-center gap-2">
                  <Eye className="w-4 h-4 text-primary" />
                  Folder Preview
                </h4>
                <button onClick={() => { setPreviewPath(null); setPreview(null); }} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
              </div>
              {previewLoading ? (
                <div className="flex justify-center py-4"><RefreshCw className="w-5 h-5 animate-spin text-primary" /></div>
              ) : preview ? (
                <>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-white rounded-lg p-3 border">
                      <p className="text-2xl font-bold text-primaryDark">{preview.total_files}</p>
                      <p className="text-xs text-gray-500">Files</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border">
                      <p className="text-2xl font-bold text-primaryDark">{Object.keys(preview.by_extension || {}).length}</p>
                      <p className="text-xs text-gray-500">Types</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border">
                      <p className="text-2xl font-bold text-primaryDark">{formatSize(preview.total_size_bytes || 0)}</p>
                      <p className="text-xs text-gray-500">Total Size</p>
                    </div>
                  </div>
                  {preview.files && preview.files.length > 0 && (
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {preview.files.slice(0, 20).map((f, i) => (
                        <div key={i} className="flex items-center justify-between text-xs px-2 py-1">
                          <span className="text-gray-700 truncate flex-1">{f.name}</span>
                          <span className="text-gray-400 ml-2">{formatSize(f.size)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-gray-500 text-sm text-center py-2">Could not load preview</p>
              )}
            </div>
          )}

          {/* Scan result */}
          {scanResult && !scanResult.error && (
            <div className="rounded-lg border border-green-200 bg-green-50 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-green-200 bg-green-100/50">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800 text-sm">
                  {scanResult.documents_processed ?? 0} document{(scanResult.documents_processed ?? 0) !== 1 ? 's' : ''} processed
                </span>
              </div>
              {scanResult.documents && scanResult.documents.length > 0 && (
                <div className="divide-y divide-green-100 max-h-48 overflow-y-auto">
                  {scanResult.documents.map((doc, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                      <span className="text-gray-700 truncate flex-1">{doc.filename}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ml-2 font-medium ${
                        doc.status === 'ok' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {doc.status === 'ok' ? 'Extracted' : doc.status === 'already_exists' ? 'Already exists' : doc.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {scanResult?.error && (
            <div className="p-4 rounded-lg bg-red-50 text-red-700 text-sm">{scanResult.error}</div>
          )}
        </div>
      </div>

      {/* Previously scanned folders */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 rounded-t-lg">
          <h2 className="text-base font-semibold text-gray-900">Scanned Folders</h2>
          <p className="text-sm text-gray-500 mt-0.5">Click a folder to preview, or re-scan to refresh its contents</p>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-primary" /></div>
          ) : folders.length === 0 ? (
            <p className="text-gray-500 text-center py-8 text-sm">No folders scanned yet. Use Browse or type a path above to get started.</p>
          ) : (
            <div className="space-y-2">
              {folders.map((f) => (
                <div key={f.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-blue-50 transition-colors group">
                  <button className="flex-1 text-left" onClick={() => { setFolderPath(f.path); setPreviewPath(f.path); }}>
                    <span className="font-medium text-gray-900 group-hover:text-primaryDark text-sm">{displayUploadPath(f.path)}</span>
                    <span className="ml-3 text-sm text-gray-500">{f.document_count ?? f.file_count ?? 0} files</span>
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">
                      {f.scan_date ? new Date(f.scan_date).toLocaleString() : f.scanned_at ? new Date(f.scanned_at).toLocaleString() : ''}
                    </span>
                    <button
                      onClick={() => handleScan(f.path)}
                      disabled={scanning}
                      className="p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-blue-100"
                      title="Re-scan"
                    >
                      <RefreshCw className="w-4 h-4 text-primary" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showBrowser && (
        <FolderBrowser
          onSelect={handleBrowseSelect}
          onClose={() => setShowBrowser(false)}
        />
      )}
    </div>
  );
};

export default ProjectList;
